import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { knowledgeBaseService } from '../services/knowledgeBaseService';
import type { KnowledgeBase } from '../services/knowledgeBaseService';
import { fieldUpdateService, type FieldUpdateEstimateResponse } from '../services/fieldUpdateService';

interface Props {
  isOpen: boolean;
  subProjectId: string;
  fieldName: string;
  onClose: () => void;
  onLaunched: (runId: string) => void;
}

type Step = 'select_kb' | 'confirm_cost' | 'launching';

const FieldUpdateLaunchModal: React.FC<Props> = ({ isOpen, subProjectId, fieldName, onClose, onLaunched }) => {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('select_kb');
  const [availableKBs, setAvailableKBs] = useState<KnowledgeBase[]>([]);
  const [loadingKBs, setLoadingKBs] = useState(false);
  const [selectedKbId, setSelectedKbId] = useState('');
  const [intent, setIntent] = useState('');
  const [estimate, setEstimate] = useState<FieldUpdateEstimateResponse | null>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep('select_kb');
    setSelectedKbId('');
    setIntent('');
    setEstimate(null);
    setError(null);

    const load = async () => {
      setLoadingKBs(true);
      try {
        const all = await knowledgeBaseService.getAll();
        setAvailableKBs(all);
        if (all.length === 1) setSelectedKbId(all[0].id);
      } finally {
        setLoadingKBs(false);
      }
    };
    load();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleEstimate = async () => {
    if (!selectedKbId) return;
    setLoadingEstimate(true);
    setError(null);
    try {
      const est = await fieldUpdateService.estimate(subProjectId, fieldName, selectedKbId);
      setEstimate(est);
      setStep('confirm_cost');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('fieldUpdate.estimateError', 'Failed to estimate cost'));
    } finally {
      setLoadingEstimate(false);
    }
  };

  const handleLaunch = async () => {
    if (!selectedKbId) return;
    setLaunching(true);
    setError(null);
    try {
      const resp = await fieldUpdateService.launch(subProjectId, fieldName, {
        kb_id: selectedKbId,
        intent: intent.trim() || undefined,
      });
      onLaunched(resp.run_id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('fieldUpdate.launchError', 'Failed to launch'));
      setLaunching(false);
    }
  };

  const costDollars = estimate ? (estimate.estimated_cost_cents / 100).toFixed(2) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-base font-semibold text-slate-800 mb-4">
          {t('fieldUpdate.modalTitle', 'Update field from Knowledge Base')}
        </h2>

        {step === 'select_kb' && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              {t('fieldUpdate.modalHint', 'Select a Knowledge Base. The pipeline will find relevant ideas and propose an updated field draft.')}
            </p>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {t('fieldUpdate.sourceKb', 'Source Knowledge Base')}
              </label>
              {loadingKBs ? (
                <p className="text-sm text-slate-400">{t('common.loading', 'Loading...')}</p>
              ) : availableKBs.length === 0 ? (
                <p className="text-sm text-slate-500">{t('fieldUpdate.noKbs', 'No Knowledge Bases available.')}</p>
              ) : (
                <select
                  value={selectedKbId}
                  onChange={e => setSelectedKbId(e.target.value)}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                >
                  <option value="" disabled>{t('fieldUpdate.selectKbPlaceholder', 'Select a Knowledge Base...')}</option>
                  {availableKBs.map(kb => (
                    <option key={kb.id} value={kb.id}>
                      {kb.name} ({kb.document_count ?? 0} {t('fieldUpdate.docs', 'docs')})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {t('fieldUpdate.intent', 'Intent')}
                <span className="ml-1 text-slate-400 font-normal">{t('fieldUpdate.optional', '(optional)')}</span>
              </label>
              <textarea
                rows={2}
                value={intent}
                onChange={e => setIntent(e.target.value)}
                placeholder={t('fieldUpdate.intentPlaceholder', 'Describe what you want to find, e.g. "novel thermal management approaches"...')}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm resize-none"
              />
            </div>

            {error && <p className="text-xs text-red-500">{error}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800">
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleEstimate}
                disabled={!selectedKbId || loadingEstimate}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loadingEstimate ? t('common.loading', 'Loading...') : t('fieldUpdate.estimateBtn', 'Estimate cost')}
              </button>
            </div>
          </div>
        )}

        {step === 'confirm_cost' && estimate && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">{t('fieldUpdate.estimatedChunks', 'Relevant chunks')}</span><span className="font-medium">{estimate.estimated_chunks}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t('fieldUpdate.estimatedClusters', 'Idea clusters')}</span><span className="font-medium">~{estimate.estimated_clusters}</span></div>
              <div className="flex justify-between text-base"><span className="text-slate-700 font-medium">{t('fieldUpdate.estimatedCost', 'Estimated cost')}</span><span className="font-semibold text-slate-800">${costDollars}</span></div>
            </div>
            <p className="text-xs text-slate-400">
              {t('fieldUpdate.costDisclaimer', 'Estimate is approximate. Actual cost depends on cluster count and field length.')}
            </p>

            {error && <p className="text-xs text-red-500">{error}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setStep('select_kb')} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800">
                {t('common.back', 'Back')}
              </button>
              <button
                onClick={handleLaunch}
                disabled={launching}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {launching ? t('fieldUpdate.launching', 'Launching...') : t('fieldUpdate.launchBtn', 'Launch')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldUpdateLaunchModal;
